import {
  Upload,
  Search,
  Sparkles,
  FileSignature,
  FileText,
  Receipt,
  Calendar,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { Seo } from "../lib/head";
import { RelatedLinks } from "../components/marketing";

const FLOW = [
  {
    icon: Upload,
    title: "Chart ingest",
    body: "Upload facility charts in common formats. Data stays within your tenant boundary with a full audit trail.",
  },
  {
    icon: Search,
    title: "PHI de-identification",
    body: "Resident identifiers are removed before any AI-assisted step. Every de-identification event is logged.",
  },
  {
    icon: Sparkles,
    title: "Clinical engine + AI synthesis",
    body: "Evidence-cited clinical flags fire first. AI helps draft recommendations — clinical facts are never extracted by the model.",
  },
  {
    icon: FileText,
    title: "Bundle render",
    body: "Professional recommendation bundles with citations and facility-level summaries, ready for medical director review.",
  },
  {
    icon: FileSignature,
    title: "Adobe Sign workflow",
    body: "Embedded signing for medical director sign-off. Status updates automatically when signing completes.",
  },
  {
    icon: Receipt,
    title: "QBO invoice draft",
    body: "Draft invoices from time entries and completed bundles. Billing metadata stays facility-level — no PHI on line items.",
  },
  {
    icon: Calendar,
    title: "Calendar + reminders",
    body: "Sync review deadlines with Google or Outlook. License and filing reminders keep compliance on schedule.",
  },
];

export function Platform() {
  return (
    <>
      <Seo title="Chart Review Automation Platform | WeConsult Rx" description="Chart review automation for consultant pharmacists, ingest to invoice: chart ingest, PHI de-identification, clinical engine, AI synthesis, bundle render, e-sign, QBO invoicing, and calendar sync." path="/platform" />
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Platform</Eyebrow>
            <Heading level={1} className="mt-3">
              One workflow, end to end.
            </Heading>
            <Lede className="mt-6">
              From chart ingest to signed recommendation bundle to invoiced
              billable hour &mdash; with PHI safety enforced in code at every
              step.
            </Lede>
            <div className="mt-8 flex gap-3">
              <LinkButton to="/demo">Request a demo</LinkButton>
              <LinkButton to="/features" variant="secondary">
                Feature deep-dive
              </LinkButton>
            </div>
          </div>
        </Container>
      </Section>

      <Section className="pt-8" tone="soft">
        <Container>
          <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" aria-label="Workflow steps">
            {FLOW.map((f, i) => {
              const Icon = f.icon;
              return (
                <Card as="li" key={f.title}>
                  <div className="flex items-center gap-3">
                    <span className="text-xs uppercase tracking-tagline text-forest-700 font-semibold">
                      Step {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="mt-3 flex h-11 w-11 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-4">
                    {f.title}
                  </Heading>
                  <p className="mt-2 text-ink-600 text-sm">{f.body}</p>
                </Card>
              );
            })}
          </ol>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <Eyebrow>Built for healthcare</Eyebrow>
              <Heading level={2} className="mt-2">
                Compliance and clinical rigor, by design.
              </Heading>
              <p className="mt-4 text-ink-600">
                Multi-tenant isolation, encryption at rest and in transit, and
                HIPAA-aligned controls are part of the product — not an
                afterthought.
              </p>
              <ul className="mt-6 space-y-3 text-sm text-ink-800">
                {[
                  "Tenant-scoped data with role-based access",
                  "De-identified AI prompts — no LLM extraction of clinical facts",
                  "Adobe Sign for embedded medical director sign-off",
                  "Opt-in email and SMS reminders",
                  "QuickBooks Online invoice drafting",
                  "Append-only audit log with long-term retention",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-forest-700 mt-0.5" aria-hidden />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="bg-forest-gradient text-white border-transparent">
              <Badge tone="neutral" className="bg-white/15 border-white/20 text-white">
                What you get
              </Badge>
              <div className="mt-6 grid gap-3 text-sm">
                {[
                  ["Chart review", "Ingest → flag → pharmacist decision"],
                  ["Recommendations", "Evidence-cited, auditable bundles"],
                  ["Sign-off", "Embedded e-signature workflow"],
                  ["Billing", "Time tracking + invoice drafts"],
                  ["Compliance", "Audit trail on every action"],
                  ["Integrations", "Adobe Sign · QBO · calendar sync"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-center justify-between gap-3 rounded-xl bg-white/10 px-4 py-3"
                  >
                    <span className="font-semibold">{k}</span>
                    <span className="text-white/85 text-right">{v}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
          <div className="mt-12 text-center">
            <LinkButton to="/security" variant="secondary">
              Security overview
              <ArrowRight className="h-4 w-4" aria-hidden />
            </LinkButton>
          </div>
        </Container>
      </Section>
      <RelatedLinks
        items={[
          { to: "/medication-regimen-review-software", label: "MRR software", desc: "The monthly review, chart ingest to invoice." },
          { to: "/consultant-pharmacist-software", label: "Consultant pharmacist software", desc: "SNF, ASC, and dialysis on one platform." },
          { to: "/pricing", label: "Pricing", desc: "Plans from solo to multi-facility." },
        ]}
      />
    </>
  );
}
